import React, { useEffect, useRef } from "react"
import {
    ActivityIndicator,
    View,
    StyleSheet,
    Text,
    TouchableOpacity,
    Animated,
    Easing,
} from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import StageScarcityMeter from "./StageScarcityMeter"
import FunnelGoalDial from "@common/components/funnel/FunnelGoalDial"
import StageClosingCountdown from "./StageClosingCountdown"
import FunnelToFix from "@common/components/funnel/FunnelToFix"
import FunnelStepBullet from "@common/components/funnel/FunnelStepBullet"
import FunnelChoiceChip from "@common/components/funnel/FunnelChoiceChip"
import FunnelSubjectChips from "@common/components/funnel/FunnelSubjectChips"
import FunnelSubjectHoursSplit from "@common/components/funnel/FunnelSubjectHoursSplit"
import FunnelReveal from "@common/components/funnel/FunnelReveal"

/**
 * Composition d'un stage vendu AU CRÉNEAU (Week-end Réussite).
 *
 * UN SEUL week-end est proposé : le prochain encore ouvert. Montrer celui dans
 * trois semaines allongerait une décision qu'on veut immédiate, et le parent
 * qui en veut plus reviendra le week-end suivant. Le week-end qui vient de
 * fermer reste visible **une ligne, barrée** — il explique pourquoi on propose
 * le suivant, au lieu de disparaître sans un mot.
 *
 * Trois questions empilées dans un écran, dans cet ordre :
 *   1. les MATIÈRES — on ouvre sur ce que le parent veut pour son enfant ;
 *   2. l'OBJECTIF visé sur chacune, matière par matière ;
 *   3. les CRÉNEAUX, et seulement là.
 *
 * **Un créneau accueille UNE matière.** Choisir deux matières et un seul
 * créneau laissait la seconde à zéro heure — et la ligne de répartition, qui
 * masque les zéros, ne le disait même pas. Le compositeur réclame donc au moins
 * autant de créneaux que de matières, et le dit à l'endroit où ça se corrige.
 *
 * Le parent ne saisit JAMAIS un nombre d'heures : la répartition est automatique
 * et affichée en clair. Son seul recours est « Répartir autrement », discret —
 * le contrôle qui existe déjà sur le reveal du programme d'été, réutilisé tel
 * quel. On affiche des HEURES et non des créneaux, parce que c'est ce que parle
 * ce contrôle et ce que stocke le backend : une répartition à la main peut
 * couper un créneau en deux matières, et « 1,5 créneau » ne se dit pas.
 *
 * Le cœur du compositeur n'est PAS encapsulé dans une carte : trois questions
 * empilées, chacune déjà dense, entourées d'un cadre, donnaient un écran qui
 * pèse. Il vit dans le corps de la page ; seules les cartes d'objectif et les
 * créneaux gardent une bordure, parce qu'eux sont des choix.
 *
 * Purement présentationnel — tout l'état vit dans {@see StageConfiguratorScreen}.
 */
const StageWeekendComposer = ({
    group,
    closedGroup,
    selectedKeys,
    subjectIds,
    subjectById,
    subjectOptions = [],
    maxSubjects = 1,
    subjectsLoading = false,
    onToggleSubject,
    subjectGoals,
    hoursBySubject,
    canAddMore,
    scarcity,
    onToggleSlot,
    onSetGoal,
    onOpenRedistribute,
    sectionToFix,
    /** Change à chaque tentative bloquée : le rappel tremble à nouveau. */
    toFixNudge = 0,
    onSectionLayout,
    sectionRef,
}) => {
    const hasSubjects = subjectIds.length > 0
    const selectedCount = group
        ? group.items.filter((item) => selectedKeys.includes(item.key)).length
        : 0
    // Un créneau, une matière : en dessous, une matière repartirait à zéro heure.
    const missingSlots = Math.max(0, subjectIds.length - selectedCount)

    if (!group) return null

    return (
        <View style={styles.wrap}>
            {closedGroup ? (
                <View style={styles.closedRow}>
                    <FontAwesome5
                        name="lock"
                        size={11}
                        color={theme.colors.grey}
                    />
                    <Text style={styles.closedLabel}>{closedGroup.label}</Text>
                    <Text style={styles.closedFlag}>Complet</Text>
                </View>
            ) : null}

            {/* Le week-end, en une ligne sous le titre de l'écran : le reste
                (jours, durée, urgence) se lit là où le parent choisit. */}
            <Text style={styles.dateLine}>{group.label}</Text>

            {/* 1 — les matières */}
            <View
                ref={sectionRef?.("subjects")}
                onLayout={onSectionLayout?.("subjects")}
            />
            <FunnelStepBullet n={1} label="Quelles matières ?" />
            {sectionToFix === "subjects" ? (
                <FunnelToFix
                    message="Choisissez au moins une matière."
                    nudge={toFixNudge}
                />
            ) : null}
            {/* Toutes les matières proposables, cochables ici même : le parent
                les VOIT avant de choisir, sans ouvrir une modale. La sélection
                se limite en silence — au plafond, les chips restantes se
                grisent, le nombre n'est jamais annoncé. */}
            <FunnelSubjectChips
                options={subjectOptions}
                selectedIds={subjectIds.map(String)}
                maxSubjects={maxSubjects}
                onToggle={onToggleSubject}
                loading={subjectsLoading}
            />

            {/* Les blocs 2 et 3 n'apparaissent qu'une fois le précédent
                renseigné : le parent découvre l'écran par paliers. */}
            <FunnelReveal visible={hasSubjects}>
                {/* 2 — l'objectif, matière par matière */}
                <FunnelStepBullet
                    n={2}
                    label="Quel objectif au prochain contrôle ?"
                />
                {subjectIds.map((id) => (
                    <FunnelGoalDial
                        key={id}
                        subjectName={
                            subjectById.get(String(id))?.label ?? "Matière"
                        }
                        value={subjectGoals[String(id)]}
                        onChange={(slug) => onSetGoal(id, slug)}
                    />
                ))}

                {/* 3 — les créneaux */}
                <View
                    ref={sectionRef?.("slots")}
                    onLayout={onSectionLayout?.("slots")}
                />
                <FunnelStepBullet n={3} label="Quand ?" />
                {scarcity ? (
                    <View style={styles.meterRow}>
                        <StageScarcityMeter
                            places={scarcity.places}
                            gaugeFill={scarcity.gaugeFill}
                            compact
                        />
                    </View>
                ) : null}
                <StageClosingCountdown closesAt={group.closesAt} />
                {sectionToFix === "slots" ? (
                    <FunnelToFix
                        nudge={toFixNudge}
                        message={
                            selectedCount === 0
                                ? "Cochez au moins un créneau."
                                : `Un créneau accueille une matière : cochez-en ${missingSlots} de plus, ou retirez une matière.`
                        }
                    />
                ) : null}
                <View style={styles.slotList}>
                    {group.items.map((item) => {
                        const selected = selectedKeys.includes(item.key)
                        const locked = item.full || (!selected && !canAddMore)
                        return (
                            <SlotRow
                                key={item.key}
                                item={item}
                                selected={selected}
                                locked={locked}
                                onPress={() => onToggleSlot(item.key)}
                            />
                        )
                    })}
                </View>

                {selectedCount > 0 ? (
                    <View style={styles.split}>
                        {/* Rien à répartir quand chaque matière a exactement un
                                créneau : le seul partage possible est celui qu'on
                                affiche. Le lien n'apparaît qu'avec un créneau de
                                marge — et il répartit par créneau entier. */}
                        <FunnelSubjectHoursSplit
                            subjectIds={subjectIds}
                            subjectById={subjectById}
                            hoursBySubject={hoursBySubject}
                            canRedistribute={
                                subjectIds.length > 1 &&
                                selectedKeys.length > subjectIds.length
                            }
                            onOpenRedistribute={onOpenRedistribute}
                        />
                    </View>
                ) : null}
            </FunnelReveal>
        </View>
    )
}

/**
 * Un créneau cochable. La coche « rebondit » à la sélection — c'est le seul
 * geste que le parent répète, il mérite d'être agréable.
 */
const SlotRow = ({ item, selected, locked, onPress }) => {
    const pop = useRef(new Animated.Value(1)).current
    const first = useRef(true)

    useEffect(() => {
        if (first.current) {
            first.current = false
            return
        }
        Animated.sequence([
            Animated.timing(pop, {
                toValue: selected ? 1.16 : 0.94,
                duration: 100,
                easing: Easing.out(Easing.quad),
                useNativeDriver: true,
            }),
            Animated.spring(pop, {
                toValue: 1,
                friction: 4,
                tension: 160,
                useNativeDriver: true,
            }),
        ]).start()
    }, [selected])

    return (
        <TouchableOpacity
            activeOpacity={0.8}
            disabled={locked}
            onPress={onPress}
            style={[
                styles.slotCard,
                selected && styles.slotCardSelected,
                locked && styles.slotCardLocked,
            ]}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: selected, disabled: locked }}
            accessibilityLabel={item.label}
        >
            <Animated.View style={{ transform: [{ scale: pop }] }}>
                <FontAwesome5
                    name={selected ? "check-square" : "square"}
                    solid={selected}
                    size={17}
                    color={
                        selected ? theme.colors.primary : theme.colors.lightMore
                    }
                />
            </Animated.View>
            <Text
                style={[styles.slotLabel, selected && styles.slotLabelSelected]}
            >
                {item.label}
            </Text>
            {item.full ? <Text style={styles.slotFull}>Complet</Text> : null}
        </TouchableOpacity>
    )
}

const styles = StyleSheet.create({
    wrap: { gap: 10 },

    // Le week-end fermé : une ligne, discrète, barrée. Elle explique pourquoi on
    // propose le suivant — elle ne se dispute pas l'attention avec lui.
    closedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 10,
        paddingHorizontal: 13,
        borderRadius: 12,
        backgroundColor: theme.colors.greyBg,
    },
    closedLabel: {
        flex: 1,
        fontFamily: "Montserrat-Medium",
        fontSize: 12.5,
        color: theme.colors.grey,
        textDecorationLine: "line-through",
    },
    closedFlag: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 9.5,
        letterSpacing: 0.7,
        color: theme.colors.grey,
    },

    dateLine: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
        marginTop: -6,
    },
    meterRow: { marginTop: -6, marginBottom: 12 },

    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },

    slotList: { gap: 8 },
    slotCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingVertical: 13,
        paddingHorizontal: 14,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        backgroundColor: theme.colors.white,
    },
    slotCardSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    slotCardLocked: { opacity: 0.4 },
    slotLabel: {
        flex: 1,
        fontFamily: "Montserrat-Medium",
        fontSize: 14,
        color: theme.colors.black,
    },
    slotLabelSelected: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    slotFull: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 9.5,
        letterSpacing: 0.7,
        color: theme.colors.error,
    },

    // Le récap de répartition : une note de bas de carte, pas un bloc de plus.
    split: {
        marginTop: 16,
        paddingTop: 13,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
    },
})

export default StageWeekendComposer
